export const OTHER_GROUP_TITLE = "Other";

// Map order is render order — for the groups and for the lists inside each.
const GROUP_MAP: readonly { title: string; keys: readonly string[] }[] = [
  { title: "People", keys: ["User", "Customer", "Tradie", "PortfolioPhoto", "CompletedProject"] },
  { title: "Trades", keys: ["TradeCategory", "TradeService", "TradieLicence"] },
  { title: "Jobs", keys: ["Job", "JobPhoto", "JobUpdate", "JobEvent"] },
  { title: "Appointments", keys: ["Appointment"] },
  { title: "Quoting", keys: ["Quote", "QuoteLineItem", "QuoteRequest"] },
  { title: "Invoicing", keys: ["Invoice", "InvoiceLineItem"] },
  { title: "Payments", keys: ["Payment", "PaymentMethod", "Payout"] },
  { title: "Messaging", keys: ["Conversation", "Message", "ContactMessage"] },
  { title: "Notifications", keys: ["Notification", "NotificationPreference"] },
  { title: "Reviews", keys: ["Review", "ReviewPhoto"] },
  { title: "Platform", keys: ["PlatformSetting", "SuburbGeocode"] }
];

const MAPPED_KEYS = new Set(GROUP_MAP.flatMap((group) => group.keys));

export type NavGroup<T> = { title: string; lists: T[] };

/**
 * Buckets the server-exposed lists into sidebar groups. Every input list
 * lands in exactly one group; a key the map does not name falls into a
 * trailing "Other" group so a future list can never vanish from the nav.
 * Empty groups (Other included) are omitted.
 */
export function groupNavLists<T extends { key: string }>(lists: readonly T[]): NavGroup<T>[] {
  const byKey = new Map(lists.map((list) => [list.key, list]));

  const groups: NavGroup<T>[] = [];
  for (const { title, keys } of GROUP_MAP) {
    const members = keys.flatMap((key) => byKey.get(key) ?? []);
    if (members.length > 0) {
      groups.push({ title, lists: members });
    }
  }

  const other = lists.filter((list) => !MAPPED_KEYS.has(list.key));
  if (other.length > 0) {
    groups.push({ title: OTHER_GROUP_TITLE, lists: other });
  }

  return groups;
}
